import type {
  DirectoryLandingData,
  DirectoryWineCardData,
  VintageGuideEntry,
  WineDetailData,
  WineDetailMetaItem,
  WineryDetailData,
  WineryDetailWine,
} from "@/lib/directory-types";

interface StaticWineryRow {
  slug: string;
  name: string;
  region: string;
  established: number | null;
  description: string;
  rating: number | null;
  image: string;
  isFeatured: boolean;
  tagline?: string;
  location?: string;
  philosophy?: string;
  reviews?: number;
  details?: Record<string, string>;
  contact?: Record<string, string>;
  awards?: string[];
  visitInfo?: Record<string, string>;
}

interface StaticWineRow {
  slug: string;
  name: string;
  winery: string;
  winerySlug: string;
  vintage: number | null;
  region: string;
  type: string;
  blend: string;
  rating: number | null;
  price: string | null;
  description: string;
  production: string;
  category: string;
  tastingNotes: string[];
  details: WineDetailMetaItem[];
  pairings: string[];
  isFeatured: boolean;
  isListed: boolean;
  sortOrder: number;
}

export const DIRECTORY_STATIC_VINTAGE_GUIDE: VintageGuideEntry[] = [
  { year: 2021, rating: "Excellent", description: "Cool vintage with great freshness and structure" },
  { year: 2020, rating: "Very Good", description: "Warm year producing ripe, approachable wines" },
  { year: 2019, rating: "Excellent", description: "Balanced vintage with elegance and aging potential" },
  { year: 2018, rating: "Outstanding", description: "Classic vintage, structured wines for long aging" },
  { year: 2017, rating: "Good", description: "Early harvest, fruit-forward styles" },
];

export const DIRECTORY_STATIC_WINERY_ROWS: StaticWineryRow[] = [
  {
    slug: "domaine-du-castel",
    name: "Domaine du Castel",
    region: "Judean Hills",
    established: 1988,
    description:
      "Founded by Eli Ben Zaken, Domaine du Castel has become synonymous with Israeli wine excellence. Located at 750m elevation in the Jerusalem Hills, the estate produces Bordeaux-style wines that rival the world's best.",
    rating: 4.8,
    image: "https://images.unsplash.com/photo-1543418219-44e30b057fea?w=1200",
    isFeatured: true,
    tagline: "Pioneering Excellence in the Judean Hills",
    location: "Ramat Raziel, Jerusalem Hills",
    philosophy:
      "Terroir-driven winemaking with minimal intervention. Every decision in vineyard and cellar aims to express the unique limestone terroir of the Judean Hills.",
    reviews: 247,
    details: {
      winemaker: "Eli Ben Zaken",
      elevation: "750m",
      hectares: "15 hectares",
      production: "60,000 bottles annually",
      soils: "Limestone, terra rossa",
      climate: "Mediterranean mountain climate",
    },
    contact: {
      phone: "+972-2-534-2249",
      website: "www.castel.co.il",
      email: "info@castel.co.il",
      visitingHours: "By appointment only",
    },
    awards: [
      "Best Israeli Winery - Wine Enthusiast 2022",
      "Top 100 Wines - Wine Spectator (multiple years)",
      "Decanter World Wine Awards - Gold Medal",
      "International Wine Challenge - Gold Medal",
    ],
    visitInfo: {
      tours: "Private tours available by appointment",
      tastings: "Comprehensive tasting of current releases",
      duration: "90 minutes",
      groupSize: "Maximum 8 people",
      languages: "English, Hebrew, French",
      accessibility: "Limited wheelchair access (mountainous terrain)",
    },
  },
  {
    slug: "yatir-winery",
    name: "Yatir Winery",
    region: "Judean Hills",
    established: 2000,
    description:
      "Located at the edge of the Negev desert, producing structured wines from extreme terroir.",
    rating: 4.7,
    image: "https://images.unsplash.com/photo-1516594915697-87eb3b1c14ea?w=1200",
    isFeatured: true,
    tagline: "Desert-edge precision and structure",
    location: "Tel Arad, Southern Judean Hills",
    reviews: 168,
  },
  {
    slug: "tzora-vineyards",
    name: "Tzora Vineyards",
    region: "Judean Hills",
    established: 1993,
    description:
      "Family estate known for single-vineyard expressions and old-vine Carignan.",
    rating: 4.6,
    image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200",
    isFeatured: true,
    tagline: "Single-vineyard character from limestone hills",
    location: "Kibbutz Tzora, Jerusalem Hills",
    reviews: 121,
  },
  {
    slug: "galil-mountain-winery",
    name: "Galil Mountain Winery",
    region: "Galilee",
    established: 2000,
    description:
      "Modern facility in Upper Galilee producing Mediterranean varietals at elevation.",
    rating: 4.5,
    image: "https://images.unsplash.com/photo-1474557157379-8aa74a6ef541?w=1200",
    isFeatured: true,
    tagline: "Cool climate wines from Upper Galilee",
    location: "Yir'on, Upper Galilee",
    reviews: 113,
  },
  {
    slug: "recanati-winery",
    name: "Recanati Winery",
    region: "Galilee",
    established: 2000,
    description:
      "Pioneering Mediterranean varieties and innovative winemaking techniques.",
    rating: 4.4,
    image: "https://images.unsplash.com/photo-1523825391079-0092e2f2baaf?w=1200",
    isFeatured: true,
    tagline: "Mediterranean varieties with modern style",
    location: "Emek Hefer, Sharon",
    reviews: 98,
  },
  {
    slug: "clos-de-gat",
    name: "Clos de Gat",
    region: "Judean Hills",
    established: 1998,
    description:
      "Small production winery focusing on Syrah and Bordeaux blends from specific terroirs.",
    rating: 4.7,
    image: "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=1200",
    isFeatured: true,
    tagline: "Boutique estate with terroir-first blends",
    location: "Ayalon Valley",
    reviews: 87,
  },
  {
    slug: "golan-heights-winery",
    name: "Golan Heights Winery",
    region: "Golan Heights",
    established: null,
    description: "Premium winery known for volcanic terroir and age-worthy reds.",
    rating: 4.6,
    image: "https://images.unsplash.com/photo-1528825871115-3581a5387919?w=1200",
    isFeatured: false,
  },
  {
    slug: "flam-winery",
    name: "Flam Winery",
    region: "Judean Hills",
    established: null,
    description: "Refined Judean Hills producer focused on balance and elegance.",
    rating: 4.4,
    image: "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=1200",
    isFeatured: false,
  },
  {
    slug: "pelter-winery",
    name: "Pelter Winery",
    region: "Golan Heights",
    established: null,
    description: "Modern Mediterranean style producer with approachable blends.",
    rating: 4.3,
    image: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=1200",
    isFeatured: false,
  },
  {
    slug: "tabor-winery",
    name: "Tabor Winery",
    region: "Galilee",
    established: null,
    description: "Large producer with broad regional lineup and everyday value.",
    rating: 4.1,
    image: "https://images.unsplash.com/photo-1474722883778-792e7990302f?w=1200",
    isFeatured: false,
  },
  {
    slug: "dalton-winery",
    name: "Dalton Winery",
    region: "Galilee",
    established: null,
    description: "Upper Galilee producer focused on fresh whites and blends.",
    rating: 4.2,
    image: "https://images.unsplash.com/photo-1470158499416-75be9aa0c4db?w=1200",
    isFeatured: false,
  },
  {
    slug: "tishbi-winery",
    name: "Tishbi Winery",
    region: "Galilee",
    established: null,
    description: "Historic family winery with classic varietal offerings.",
    rating: 4.0,
    image: "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=1200",
    isFeatured: false,
  },
];

const defaultWineDetails = (blend: string): WineDetailMetaItem[] => [
  { label: "Blend", value: blend },
  { label: "Alcohol", value: "Not listed" },
  { label: "Oak", value: "Not listed" },
  { label: "Cellaring", value: "Not listed" },
];

const defaultTastingNotes = (name: string): string[] => [
  `${name} shows focused fruit and regional character.`,
  "Balanced structure with food-friendly acidity.",
  "A polished finish with evolving aromatics.",
];

const defaultPairings = ["Grilled lamb", "Roasted vegetables", "Aged cheese"];

export const DIRECTORY_STATIC_WINE_ROWS: StaticWineRow[] = [
  {
    slug: "castel-grand-vin-2017",
    name: "Castel Grand Vin",
    winery: "Domaine du Castel",
    winerySlug: "domaine-du-castel",
    vintage: 2017,
    region: "Judean Hills",
    type: "Red Blend",
    blend: "Cabernet Sauvignon, Merlot, Petit Verdot",
    rating: 96,
    price: "$65",
    description: "Elegant and complex. Black fruit, cedar, tobacco. Exceptional aging potential.",
    production: "10,000 bottles",
    category: "Premium",
    tastingNotes: defaultTastingNotes("Castel Grand Vin"),
    details: defaultWineDetails("Cabernet Sauvignon, Merlot, Petit Verdot"),
    pairings: defaultPairings,
    isFeatured: true,
    isListed: true,
    sortOrder: 1,
  },
  {
    slug: "yarden-katzrin-2016",
    name: "Yarden Katzrin",
    winery: "Golan Heights Winery",
    winerySlug: "golan-heights-winery",
    vintage: 2016,
    region: "Golan Heights",
    type: "Red Blend",
    blend: "Cabernet Sauvignon, Merlot",
    rating: 94,
    price: "$55",
    description: "Structured and age-worthy. Volcanic terroir character. Dark fruit, graphite.",
    production: "15,000 bottles",
    category: "Premium",
    tastingNotes: defaultTastingNotes("Yarden Katzrin"),
    details: defaultWineDetails("Cabernet Sauvignon, Merlot"),
    pairings: defaultPairings,
    isFeatured: false,
    isListed: true,
    sortOrder: 2,
  },
  {
    slug: "flam-reserve-2018",
    name: "Flam Reserve",
    winery: "Flam Winery",
    winerySlug: "flam-winery",
    vintage: 2018,
    region: "Judean Hills",
    type: "Red Blend",
    blend: "Syrah, Cabernet Sauvignon",
    rating: 93,
    price: "$48",
    description: "Refined and balanced. Mediterranean herbs, black pepper, dark berries.",
    production: "8,000 bottles",
    category: "Premium",
    tastingNotes: defaultTastingNotes("Flam Reserve"),
    details: defaultWineDetails("Syrah, Cabernet Sauvignon"),
    pairings: defaultPairings,
    isFeatured: false,
    isListed: true,
    sortOrder: 3,
  },
  {
    slug: "tzora-shoresh-2017",
    name: "Tzora Shoresh",
    winery: "Tzora Vineyards",
    winerySlug: "tzora-vineyards",
    vintage: 2017,
    region: "Judean Hills",
    type: "Red Blend",
    blend: "Cabernet Sauvignon, Syrah, Merlot",
    rating: 95,
    price: "$58",
    description: "Single vineyard excellence. Limestone minerality, elegant tannins.",
    production: "5,000 bottles",
    category: "Premium",
    tastingNotes: defaultTastingNotes("Tzora Shoresh"),
    details: defaultWineDetails("Cabernet Sauvignon, Syrah, Merlot"),
    pairings: defaultPairings,
    isFeatured: false,
    isListed: true,
    sortOrder: 4,
  },
  {
    slug: "galil-mountain-yiron-2018",
    name: "Galil Mountain Yiron",
    winery: "Galil Mountain Winery",
    winerySlug: "galil-mountain-winery",
    vintage: 2018,
    region: "Galilee",
    type: "Red Blend",
    blend: "Cabernet Sauvignon, Merlot, Syrah",
    rating: 92,
    price: "$42",
    description: "Cool climate elegance. Red fruit, fresh acidity, fine tannins.",
    production: "12,000 bottles",
    category: "Premium",
    tastingNotes: defaultTastingNotes("Galil Mountain Yiron"),
    details: defaultWineDetails("Cabernet Sauvignon, Merlot, Syrah"),
    pairings: defaultPairings,
    isFeatured: false,
    isListed: true,
    sortOrder: 5,
  },
  {
    slug: "recanati-yasmin-white-2020",
    name: "Recanati Yasmin White",
    winery: "Recanati Winery",
    winerySlug: "recanati-winery",
    vintage: 2020,
    region: "Galilee",
    type: "White Blend",
    blend: "Sauvignon Blanc, Chardonnay, Gewurztraminer",
    rating: 90,
    price: "$28",
    description: "Aromatic and fresh. Citrus, white flowers, mineral finish.",
    production: "20,000 bottles",
    category: "Everyday",
    tastingNotes: defaultTastingNotes("Recanati Yasmin White"),
    details: defaultWineDetails("Sauvignon Blanc, Chardonnay, Gewurztraminer"),
    pairings: ["Seafood", "Salads", "Fresh cheeses"],
    isFeatured: false,
    isListed: true,
    sortOrder: 6,
  },
  {
    slug: "clos-de-gat-syrah-2017",
    name: "Clos de Gat Syrah",
    winery: "Clos de Gat",
    winerySlug: "clos-de-gat",
    vintage: 2017,
    region: "Judean Hills",
    type: "Syrah",
    blend: "100% Syrah",
    rating: 94,
    price: "$52",
    description: "Pure Syrah expression. Black pepper, olive, smoked meat, refined structure.",
    production: "6,000 bottles",
    category: "Premium",
    tastingNotes: defaultTastingNotes("Clos de Gat Syrah"),
    details: defaultWineDetails("100% Syrah"),
    pairings: ["Braised lamb", "Mushroom dishes", "Hard cheeses"],
    isFeatured: false,
    isListed: true,
    sortOrder: 7,
  },
  {
    slug: "pelter-trio-2019",
    name: "Pelter Trio",
    winery: "Pelter Winery",
    winerySlug: "pelter-winery",
    vintage: 2019,
    region: "Golan Heights",
    type: "Red Blend",
    blend: "Syrah, Carignan, Petite Sirah",
    rating: 91,
    price: "$38",
    description: "Modern Mediterranean style. Juicy fruit, vibrant acidity, soft tannins.",
    production: "10,000 bottles",
    category: "Everyday",
    tastingNotes: defaultTastingNotes("Pelter Trio"),
    details: defaultWineDetails("Syrah, Carignan, Petite Sirah"),
    pairings: defaultPairings,
    isFeatured: false,
    isListed: true,
    sortOrder: 8,
  },
  {
    slug: "tabor-adama-2019",
    name: "Tabor Adama",
    winery: "Tabor Winery",
    winerySlug: "tabor-winery",
    vintage: 2019,
    region: "Galilee",
    type: "Red Blend",
    blend: "Cabernet Sauvignon, Shiraz, Merlot",
    rating: 89,
    price: "$32",
    description: "Approachable and fruit-forward. Ripe berries, oak spice, smooth finish.",
    production: "25,000 bottles",
    category: "Everyday",
    tastingNotes: defaultTastingNotes("Tabor Adama"),
    details: defaultWineDetails("Cabernet Sauvignon, Shiraz, Merlot"),
    pairings: defaultPairings,
    isFeatured: false,
    isListed: true,
    sortOrder: 9,
  },
  {
    slug: "yatir-forest-2016",
    name: "Yatir Forest",
    winery: "Yatir Winery",
    winerySlug: "yatir-winery",
    vintage: 2016,
    region: "Negev",
    type: "Red Blend",
    blend: "Cabernet Sauvignon, Petit Verdot, Merlot",
    rating: 93,
    price: "$45",
    description: "Desert terroir. Intense fruit, unique mineral character, long finish.",
    production: "18,000 bottles",
    category: "Premium",
    tastingNotes: defaultTastingNotes("Yatir Forest"),
    details: defaultWineDetails("Cabernet Sauvignon, Petit Verdot, Merlot"),
    pairings: defaultPairings,
    isFeatured: false,
    isListed: true,
    sortOrder: 10,
  },
  {
    slug: "dalton-canaan-white-2021",
    name: "Dalton Canaan White",
    winery: "Dalton Winery",
    winerySlug: "dalton-winery",
    vintage: 2021,
    region: "Galilee",
    type: "White Blend",
    blend: "Sauvignon Blanc, Viognier, Chardonnay",
    rating: 88,
    price: "$24",
    description: "Fresh and lively. Tropical fruit, citrus zest, crisp acidity.",
    production: "30,000 bottles",
    category: "Everyday",
    tastingNotes: defaultTastingNotes("Dalton Canaan White"),
    details: defaultWineDetails("Sauvignon Blanc, Viognier, Chardonnay"),
    pairings: ["Sea bass", "Soft cheeses", "Light mezze"],
    isFeatured: false,
    isListed: true,
    sortOrder: 11,
  },
  {
    slug: "tishbi-chardonnay-reserve-2019",
    name: "Tishbi Chardonnay Reserve",
    winery: "Tishbi Winery",
    winerySlug: "tishbi-winery",
    vintage: 2019,
    region: "Galilee",
    type: "Chardonnay",
    blend: "100% Chardonnay",
    rating: 91,
    price: "$36",
    description: "Old-vine Chardonnay. Citrus, minerality, subtle oak, excellent balance.",
    production: "8,000 bottles",
    category: "Premium",
    tastingNotes: defaultTastingNotes("Tishbi Chardonnay Reserve"),
    details: defaultWineDetails("100% Chardonnay"),
    pairings: ["Roasted chicken", "Creamy pasta", "Semi-hard cheeses"],
    isFeatured: false,
    isListed: true,
    sortOrder: 12,
  },
  {
    slug: "yatir-forest-2018",
    name: "Yatir Forest",
    winery: "Yatir Winery",
    winerySlug: "yatir-winery",
    vintage: 2018,
    region: "Judean Hills",
    type: "Red Blend",
    blend: "Cabernet Sauvignon, Merlot, Petit Verdot",
    rating: 95,
    price: "$45",
    description:
      "Flagship Bordeaux blend from desert-edge vineyards. Structured and age-worthy with dark fruit, cedar, and Mediterranean herbs.",
    production: "18,000 bottles",
    category: "Premium",
    tastingNotes: [
      "Black cherry, cassis, and graphite",
      "Cedar, tobacco, and wild herbs",
      "Firm tannins with a long, mineral finish",
    ],
    details: [
      { label: "Blend", value: "Cabernet Sauvignon, Merlot, Petit Verdot" },
      { label: "Alcohol", value: "14.5%" },
      { label: "Oak", value: "18 months, French oak" },
      { label: "Cellaring", value: "2026-2036" },
    ],
    pairings: ["Lamb shoulder", "Grilled eggplant", "Aged Manchego"],
    isFeatured: true,
    isListed: false,
    sortOrder: 101,
  },
  {
    slug: "tzora-shoresh-2019",
    name: "Tzora Shoresh",
    winery: "Tzora Vineyards",
    winerySlug: "tzora-vineyards",
    vintage: 2019,
    region: "Judean Hills",
    type: "Red Blend",
    blend: "Cabernet Sauvignon, Syrah, Merlot",
    rating: 93,
    price: "$38",
    description: "Single-vineyard expression. Mineral-driven with Mediterranean character.",
    production: "5,500 bottles",
    category: "Premium",
    tastingNotes: defaultTastingNotes("Tzora Shoresh"),
    details: defaultWineDetails("Cabernet Sauvignon, Syrah, Merlot"),
    pairings: defaultPairings,
    isFeatured: true,
    isListed: false,
    sortOrder: 102,
  },
  {
    slug: "castel-c-2019",
    name: "Castel C",
    winery: "Domaine du Castel",
    winerySlug: "domaine-du-castel",
    vintage: 2019,
    region: "Judean Hills",
    type: "Red Blend",
    blend: "Cabernet Sauvignon, Merlot",
    rating: 92,
    price: "$35",
    description: "Second wine. Approachable style with Castel DNA. Red fruits, herbs.",
    production: "25,000 bottles",
    category: "Premium",
    tastingNotes: defaultTastingNotes("Castel C"),
    details: defaultWineDetails("Cabernet Sauvignon, Merlot"),
    pairings: defaultPairings,
    isFeatured: false,
    isListed: false,
    sortOrder: 103,
  },
  {
    slug: "petit-castel-2020",
    name: "Petit Castel",
    winery: "Domaine du Castel",
    winerySlug: "domaine-du-castel",
    vintage: 2020,
    region: "Judean Hills",
    type: "Red Blend",
    blend: "Cabernet Sauvignon, Merlot, Cabernet Franc",
    rating: 90,
    price: "$22",
    description:
      "Third wine. Fresh, fruity, drink young. Great introduction to the estate.",
    production: "25,000 bottles",
    category: "Everyday",
    tastingNotes: defaultTastingNotes("Petit Castel"),
    details: defaultWineDetails("Cabernet Sauvignon, Merlot, Cabernet Franc"),
    pairings: defaultPairings,
    isFeatured: false,
    isListed: false,
    sortOrder: 104,
  },
];

const wineryBySlug = new Map(
  DIRECTORY_STATIC_WINERY_ROWS.map((winery) => [winery.slug, winery]),
);

const winesForWinery = (slug: string): WineryDetailWine[] =>
  DIRECTORY_STATIC_WINE_ROWS
    .filter((wine) => wine.winerySlug === slug)
    .sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0))
    .map((wine) => ({
      slug: wine.slug,
      name: wine.name,
      vintage: wine.vintage,
      type: wine.type,
      blend: wine.blend,
      rating: wine.rating,
      price: wine.price,
      description: wine.description,
      production: wine.production,
    }));

export function getStaticDirectoryLandingData(): DirectoryLandingData {
  return {
    wineries: DIRECTORY_STATIC_WINERY_ROWS.filter((w) => w.isFeatured)
      .slice(0, 6)
      .map((w) => ({
        slug: w.slug,
        name: w.name,
        region: w.region,
        established: w.established,
        description: w.description,
        rating: w.rating,
        image: w.image,
      })),
    featuredWines: DIRECTORY_STATIC_WINE_ROWS.filter((w) => w.isFeatured)
      .slice(0, 3)
      .map((w) => ({
        slug: w.slug,
        name: w.name,
        winery: w.winery,
        type: w.type,
        region: w.region,
        vintage: w.vintage,
        rating: w.rating,
        price: w.price,
        description: w.description,
      })),
    vintageGuide: DIRECTORY_STATIC_VINTAGE_GUIDE,
  };
}

export function getStaticWinesDirectoryData(): DirectoryWineCardData[] {
  return DIRECTORY_STATIC_WINE_ROWS.filter((wine) => wine.isListed)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((wine) => ({
      slug: wine.slug,
      name: wine.name,
      winery: wine.winery,
      winerySlug: wine.winerySlug,
      vintage: wine.vintage,
      region: wine.region,
      type: wine.type,
      blend: wine.blend,
      rating: wine.rating,
      price: wine.price,
      description: wine.description,
      production: wine.production,
      category: wine.category,
    }));
}

export function getStaticWineDetail(idOrSlug: string): WineDetailData | null {
  const direct = DIRECTORY_STATIC_WINE_ROWS.find((wine) => wine.slug === idOrSlug);
  if (direct) {
    return {
      slug: direct.slug,
      name: direct.name,
      winery: direct.winery,
      winerySlug: direct.winerySlug,
      vintage: direct.vintage,
      type: direct.type,
      region: direct.region,
      rating: direct.rating,
      price: direct.price,
      description: direct.description,
      tastingNotes: direct.tastingNotes,
      details: direct.details,
      pairings: direct.pairings,
    };
  }

  if (/^\d+$/.test(idOrSlug)) {
    const index = Number(idOrSlug) - 1;
    const list = getStaticWinesDirectoryData();
    const selected = list[index];
    if (!selected) return null;
    return getStaticWineDetail(selected.slug);
  }

  return null;
}

export function getStaticWineryDetail(slug: string): WineryDetailData | null {
  const winery = wineryBySlug.get(slug);
  if (!winery) return null;

  return {
    slug: winery.slug,
    name: winery.name,
    tagline: winery.tagline ?? `${winery.name} in ${winery.region}`,
    established: winery.established,
    region: winery.region,
    location: winery.location ?? winery.region,
    description: winery.description,
    philosophy: winery.philosophy ?? "Craft-focused winemaking with regional identity.",
    image: winery.image,
    rating: winery.rating,
    reviews: winery.reviews ?? null,
    details: winery.details ?? {},
    contact: winery.contact ?? {},
    awards: winery.awards ?? [],
    visitInfo: winery.visitInfo ?? {},
    wines: winesForWinery(winery.slug),
  };
}
